import React, { useState } from "react";
import api from "@/lib/api";
import { SparkleIcon, PaperPlaneRightIcon, LightningIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const EXAMPLES = [
  "Which orders are at risk of missing their delivery date?",
  "Which machine has the highest downtime this month?",
  "Recommend the best loom for a new 25mm woven elastic order.",
  "Forecast delivery date for SO-2026-001 at current production pace.",
  "Identify bottlenecks affecting OTIF performance.",
  "Why did production drop compared to yesterday?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (question) => {
    const query = question || q;
    if (!query.trim()) return;
    setMessages(m => [...m, { role: "user", text: query }]);
    setQ("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: query });
      setMessages(m => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      toast.error("AI request failed");
      setMessages(m => [...m, { role: "assistant", text: "I couldn't process that request. Please retry." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 flex flex-col h-full max-h-screen">
      <div className="mb-5">
        <div className="overline mb-1 flex items-center gap-2">
          <LightningIcon size={12} weight="fill" className="text-[var(--accent)]" />
          Powered by Claude Sonnet 4.5
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight text-white">MES Assistant</h1>
        <p className="text-sm text-zinc-500 mt-1">Ask about orders, delays, downtime, machine recommendations and delivery forecasts.</p>
      </div>

      <div className="flex-1 tactical-card p-6 overflow-y-auto min-h-[400px]" data-testid="ai-messages">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 rounded-sm bg-[var(--accent)]/10 border border-[var(--accent)]/30 flex items-center justify-center mb-4">
              <SparkleIcon size={22} weight="fill" className="text-[var(--accent)]" />
            </div>
            <div className="font-display text-lg font-bold text-white mb-1">Ask your MES</div>
            <div className="text-sm text-zinc-500 mb-6 max-w-md">The assistant has live context of machines, orders, plans and downtimes.</div>
            <div className="grid sm:grid-cols-2 gap-2 w-full max-w-2xl">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={ex}
                  data-testid={`ai-example-${i}`}
                  onClick={() => send(ex)}
                  className="text-left p-3 bg-[var(--bg-2)] border border-[var(--border)] rounded-sm text-sm text-zinc-300 hover:border-[var(--accent)]/50 hover:text-white"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={m.id || `${m.role}-${i}-${(m.text || "").slice(0, 24)}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-sm p-3 ${m.role === "user" ? "bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-white" : "bg-[var(--bg-2)] border border-[var(--border)] text-zinc-200"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{m.role === "user" ? "You" : "Assistant"}</div>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[var(--bg-2)] border border-[var(--border)] rounded-sm p-3">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <span className="w-2 h-2 rounded-full bg-[var(--accent)] status-pulse" />
                    Thinking…
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="mt-4 flex items-center gap-2"
      >
        <input
          data-testid="ai-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={loading}
          placeholder="Ask anything about your plant…"
          className="flex-1 h-11 px-4 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]"
        />
        <button
          data-testid="ai-send"
          type="submit"
          disabled={loading || !q.trim()}
          className="h-11 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-50"
        >
          Send <PaperPlaneRightIcon size={14} weight="fill" />
        </button>
      </form>
    </div>
  );
}
